import React from 'react';
import AntLocalization from '../../../utils/AntLocalization';
import AntLocalStorage from '../../../utils/AntLocalStorage';
import { getInitial } from '../shared';

// Shared building blocks for right-sidebar tabs: one look for empty/loading/error,
// avatars and dates, instead of per-tab variants.

var STATE_ICONS = {
    empty: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4',
    approval: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    document: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    note: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    reminder: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
    notification: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
    task: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    error: 'M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z'
};

export var SIDEBAR_TEXT = {
    loading: { vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा है...', ar: 'جارٍ التحميل...' },
    retry: { vi: 'Thử lại', en: 'Retry', fr: 'Réessayer', es: 'Reintentar', id: 'Coba lagi', ja: '再試行', zh: '重试', ko: '다시 시도', hi: 'फिर करें', ar: 'إعادة' },
    justNow: { vi: 'Vừa xong', en: 'Just now', fr: "À l'instant", es: 'Justo ahora', id: 'Baru saja', ja: 'たった今', zh: '刚刚', ko: '방금', hi: 'अभी', ar: 'الآن' },
    all: { vi: 'Tất cả', en: 'All', fr: 'Tous', es: 'Todos', id: 'Semua', ja: 'すべて', zh: '全部', ko: '전체', hi: 'सभी', ar: 'الكل' },
    loadFailed: { vi: 'Chưa tải được danh sách', en: 'List could not be loaded', fr: 'Impossible de charger la liste', es: 'No se pudo cargar la lista', id: 'Daftar tidak dapat dimuat', ja: '一覧を読み込めませんでした', zh: '无法加载列表', ko: '목록을 불러오지 못했습니다', hi: 'सूची लोड नहीं हो सकी', ar: 'تعذر تحميل القائمة' }
};

export function SidebarState(props) {
    var type = props.type || 'empty';
    var compact = props.compact ? ' rs-state--compact' : '';
    if (type === 'loading') {
        return (
            <div className={'rs-state rs-state--loading' + compact} role="status">
                <span className="rs-spinner" />
                <span>{AntLocalization.translateText(props.text || SIDEBAR_TEXT.loading)}</span>
            </div>
        );
    }
    var icon = STATE_ICONS[type === 'error' ? 'error' : props.icon] || STATE_ICONS.empty;
    return (
        <div className={'rs-state rs-state--' + type + compact} role={type === 'error' ? 'alert' : undefined}>
            {!props.compact && (
                <span className="rs-state-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d={icon} />
                    </svg>
                </span>
            )}
            <span className="rs-state-text">{AntLocalization.translateText(props.text)}</span>
            {props.onRetry && (
                <button type="button" className="rs-state-retry" onClick={props.onRetry}>
                    {AntLocalization.translateText(SIDEBAR_TEXT.retry)}
                </button>
            )}
        </div>
    );
}

export function SidebarAvatar(props) {
    if (props.src) return <img className="rs-avatar" src={props.src} alt="" />;
    return <span className="rs-avatar">{getInitial(props.name)}</span>;
}

function displayLocale() {
    var language = AntLocalStorage.getBusinessSetting('Sys_BaseLanguage');
    return language && language !== 'null' ? String(language) : undefined;
}

function toMillis(value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string' && !/^\d+$/.test(value)) {
        var parsed = Date.parse(value);
        return isNaN(parsed) ? null : parsed;
    }
    var number = Number(value);
    if (!number) return null;
    return number < 1e12 ? number * 1000 : number;
}

// Same rule as ante-web setting/sys/services/shared/format.js: Sys_DateFormat first,
// then the business country; UI language never decides the day/month order.
var DATE_FORMAT_BY_COUNTRY = {
    AE: 'DD/MM/YYYY', AU: 'DD/MM/YYYY', CN: 'YYYY-MM-DD', GB: 'DD/MM/YYYY',
    HK: 'DD/MM/YYYY', ID: 'DD/MM/YYYY', IN: 'DD/MM/YYYY', JP: 'YYYY/MM/DD',
    KH: 'DD/MM/YYYY', KR: 'YYYY-MM-DD', LA: 'DD/MM/YYYY', MM: 'DD/MM/YYYY',
    MY: 'DD/MM/YYYY', PH: 'MM/DD/YYYY', RU: 'DD.MM.YYYY', SA: 'DD/MM/YYYY',
    SG: 'DD/MM/YYYY', TH: 'DD/MM/YYYY', TW: 'YYYY/MM/DD', US: 'MM/DD/YYYY',
    VN: 'DD/MM/YYYY'
};

function businessDateFormat() {
    var format = AntLocalStorage.getBusinessSetting('Sys_DateFormat');
    if (typeof format === 'string' && /^(DD|MM|YYYY)[./-]/i.test(format)) return format.toUpperCase();
    var country = AntLocalStorage.getBusinessSetting('Sys_BaseCountry');
    return DATE_FORMAT_BY_COUNTRY[String(country || '').trim().toUpperCase()] || null;
}

function pad(value) {
    return String(value).padStart(2, '0');
}

function formatDate(date, withYear) {
    var format = businessDateFormat();
    if (!format) {
        var options = { day: '2-digit', month: '2-digit' };
        if (withYear) options.year = 'numeric';
        try {
            return new Intl.DateTimeFormat(displayLocale(), options).format(date);
        } catch (e) {
            return date.toLocaleDateString();
        }
    }
    if (!withYear) format = format.replace(/[./-]?YYYY[./-]?/, '');
    var values = { DD: pad(date.getDate()), MM: pad(date.getMonth() + 1), YYYY: String(date.getFullYear()) };
    return format.replace(/YYYY|DD|MM/g, function (part) {
        return values[part];
    });
}

// "27/09/2026 · 14:30" (order from Sys_DateFormat), as on mobile cards.
export function sidebarDate(value, withTime) {
    var ms = toMillis(value);
    if (ms === null) return '';
    var date = new Date(ms);
    var text = formatDate(date, true);
    return withTime ? text + ' · ' + sidebarTime(ms) : text;
}

// Short date without year (due dates, reminders).
export function sidebarDayMonth(value) {
    var ms = toMillis(value);
    return ms === null ? '' : formatDate(new Date(ms), false);
}

export function sidebarTime(value) {
    var ms = toMillis(value);
    if (ms === null) return '';
    var date = new Date(ms);
    return pad(date.getHours()) + ':' + pad(date.getMinutes());
}

// Relative time for lists ("5 phút trước", "5 minutes ago", "5分前") in the business language;
// older than a week falls back to the business date format.
export function sidebarRelativeTime(value) {
    var ms = toMillis(value);
    if (ms === null) return '';
    var diff = Math.floor((Date.now() - ms) / 1000);
    if (diff < 60) return AntLocalization.translateText(SIDEBAR_TEXT.justNow);
    if (diff >= 604800) return sidebarDate(ms, false);
    var unit = diff < 3600 ? ['minute', 60] : diff < 86400 ? ['hour', 3600] : ['day', 86400];
    try {
        return new Intl.RelativeTimeFormat(displayLocale(), { numeric: 'always', style: 'short' }).format(-Math.floor(diff / unit[1]), unit[0]);
    } catch (e) {
        return sidebarDate(ms, false);
    }
}
